import React, { Component, Fragment } from 'react';

import styled, { css } from 'styled-components';

import Box from '../atoms/Box/Box';
import Button from '../atoms/Button/Button';
import CSSMediaQuery, { ShowOn } from '../atoms/CSSMediaQuery/CSSMediaQuery';
import { Col, Row } from '../atoms/Grid';
import Image from '../atoms/Image/Image';
import Text from '../atoms/Text/Text';
import ResellerBanner from '../molecules/ResellerBanner';
import SelectBox from '../molecules/SelectBox';
import Title from '../molecules/Title';
import AppNameInLine from '../organisms/AppNameInLine';

import { Align } from '@shared/enums/align';
import { Color } from '@shared/enums/button-color';
import { Currency } from '@shared/interfaces/currency';
import { DESKTOP_PAGE_WIDTH, breakpoint } from 'theme';
import { I18nTranslation } from '@shared/interfaces/i18n';
import { PricingCard } from '@shared/interfaces/pricing-card';
import { Product } from '@shared/interfaces/product';
import { SelectedSubscription } from '@shared/interfaces/selected-subscription';
import { Size } from '@shared/enums/button-size';
import SliderArrowIcon from '@public/icons/sliderArrow.svg';
import { Transform } from '@shared/enums/transform';
import { _getInfoForSubscription } from '@shared/markup/functions';
import { withTranslation } from 'i18n';

const SelectBoxExtend = styled(SelectBox)`
  min-height: 16.875em;
  display: flex;
  flex-direction: column;

  ${breakpoint.m`
    min-height: 21em;
    margin-bottom: 1.75em;
    margin-left: 0.875em;
    margin-right: 0.875em;
    width: calc(50% - 1.75em);
    padding-top: 1.25em;
    padding-bottom: 1.25em;
    box-shadow: 0 0 0.75em 0 ${({ theme }) => theme.transparentColors.black15};
    text-align: center;
  `}

  ${breakpoint.lg`
    margin-left: 1.25em;
    margin-right: 1.25em;
    width: calc(50% - 2.5em);
    margin-bottom: 2.625em;
  `}
`;

const RowDesc = styled(Row)`
  flex-grow: 1;
`;

const ImageExtend = styled(Image)`
  box-shadow: 0 0.125em 1em 0 ${({ theme }) => theme.transparentColors.black25};
  border-radius: 50%;

  ${breakpoint.m`
    height: 3em;
  `}
`;

const BoxWrapperAll = styled(Box)`
  ${breakpoint.m`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  `}
`;

const WrapperOuter = styled(Box)`
  ${breakpoint.m`
    max-width: ${DESKTOP_PAGE_WIDTH}em;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  `}
`;

const BoxDesc = styled(Box)`
  flex-grow: 1;
`;

const RowPrice = styled(Row)`
  ${breakpoint.m`
    justify-content: center;
  `}
`;

const ButtonChoose = styled(Button)`
  height: 2.125em;
  width: 8.5em;
  border-radius: 0.25em;
`;

const sharedCss = css`
  display: flex;
  justify-content: flex-end;

  ${breakpoint.m`
    justify-content: center;
  `}
`;

const BoxSale = styled(Box)`
  background-color: ${({ theme }) => theme.transparentColors.flushOrange20};

  ${sharedCss};
`;

const BoxDuration = styled(Box)`
  ${sharedCss};
`;

const ColArrow = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;

  ${breakpoint.m`
    display: none;
  `}
`;

type LicensesTemplateProps = {
  application?: Product;
  onClickOption: () => void;
  cards: SelectedSubscription[];
  currency: Currency;
  prices: PricingCard[];
  t: I18nTranslation;
};

class LicensesTemplate extends Component<LicensesTemplateProps> {
  emptyShowDivider = [];

  onlyMobileSize = ['mobile'];

  render() {
    const { application, cards, currency, prices, onClickOption, t } =
      this.props;

    const resellerImage =
      application.reseller && application.reseller.image
        ? application.reseller.image
        : null;

    return (
      <Fragment>
        <AppNameInLine />

        {resellerImage && <ResellerBanner image={resellerImage} />}

        <WrapperOuter>
          <Title
            lineHeight={1.13}
            showDividerOn={
              cards.length > 1 ? this.onlyMobileSize : this.emptyShowDivider
            }
            size={2}
            lgSize={2.375}
          >
            {cards.length > 1 ? 'Choose the license that suits you best' : ''}
          </Title>
          <BoxWrapperAll
            pb={3.5}
            mPl={0.875}
            mPr={0.875}
            mPb={2.625}
            lgPl={0}
            lgPr={0}
            lgPb={2.375}
            lgMl={-1.25}
            lgMr={-1.25}
          >
            {cards.map((subscription, i) => {
              const information = _getInfoForSubscription(subscription, prices);
              const card = subscription.card;

              return (
                <SelectBoxExtend
                  key={i}
                  color="scarpaFlow"
                  pt={1.125}
                  pb={1.125}
                  mb={1}
                  onClick={onClickOption.bind(this, subscription.slug)}
                >
                  <RowDesc>
                    <Col xs>
                      <Box pl={1.125} pr={1.125}>
                        <Box pb={0.5}>
                          <ImageExtend src={card.icon} height={1.875} />
                        </Box>
                        <Box smPb={1} pb={1.875}>
                          <Text
                            weight={600}
                            lineHeight={1.5}
                            size={1.75}
                            lgSize={2}
                          >
                            {subscription.name}
                          </Text>

                          {card.subtitle && (
                            <Fragment>
                              <br />
                              <Text
                                color="darkGrey"
                                weight="normal"
                                size={1.125}
                                transform={Transform.Uppercase}
                                lgSize={1.375}
                              >
                                {card.subtitle}
                              </Text>
                            </Fragment>
                          )}
                        </Box>
                      </Box>
                      <BoxDesc pl={1.125} pr={1.125}>
                        {(card.atlas ||
                          (card.contents && card.contents.length !== 0)) && (
                          <Box pb={1.5}>
                            <Text
                              color="grayChateau"
                              lineHeight={1.6}
                              weight="bold"
                              transform={Transform.Uppercase}
                            >
                              Includes
                            </Text>
                            {card.atlas && (
                              <Box>
                                <Text lineHeight={1.8} lgSize={1}>
                                  {card.atlas}
                                </Text>
                              </Box>
                            )}
                            {card.contents &&
                              card.contents
                                .split('<br/>')
                                .map((content, contentI) => (
                                  <Box key={contentI}>
                                    <Text lineHeight={1.8} lgSize={1}>
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: content,
                                        }}
                                      />
                                    </Text>
                                  </Box>
                                ))}
                          </Box>
                        )}
                      </BoxDesc>

                      {information.price === information.regPrice &&
                        Boolean(information.price) && (
                          <Box pl={1.125} pr={1.125}>
                            <RowPrice gutter={0.875}>
                              <Col>
                                <Text
                                  size={0.875}
                                  color="darkGrey"
                                  weight={600}
                                  verticalAlign="top"
                                >
                                  {information.title}{' '}
                                </Text>
                              </Col>
                              <Col>
                                <Text color="darkGrey">
                                  {currency.symbol}
                                  {information.price.toFixed(2)}
                                </Text>
                              </Col>
                            </RowPrice>
                          </Box>
                        )}
                    </Col>

                    <ColArrow>
                      <Box pr={1}>
                        <SliderArrowIcon width={18} height={18} />
                      </Box>
                    </ColArrow>
                  </RowDesc>

                  {information.price !== information.regPrice ? (
                    <Fragment>
                      <BoxSale pt={0.625} pb={0.625} pl={1.125} pr={1.125}>
                        <Box mr={1}>
                          <Text
                            transform={Transform.Uppercase}
                            weight={600}
                            color="flushOrange"
                          >
                            {
                              t(`discounts.${information.saleDiscountType}`)
                                .variant2
                            }
                          </Text>
                        </Box>
                        <Box>
                          <Text weight={600} color="flushOrange">
                            {currency.symbol}
                            {information.price.toFixed(2)}
                          </Text>
                        </Box>
                      </BoxSale>
                      <BoxDuration pt={0.625} pb={0.625} pl={1.125} pr={1.125}>
                        <Box mr={1}>
                          <Text weight={600} size={0.75} color="grayChateau">
                            {information.title}
                          </Text>
                        </Box>
                        <Box>
                          <Text color="grayChateau">
                            {currency.symbol}
                            {information.regPrice.toFixed(2)}
                          </Text>
                        </Box>
                      </BoxDuration>
                    </Fragment>
                  ) : null}

                  <CSSMediaQuery showOn={[ShowOn.Tablet, ShowOn.Desktop]}>
                    <Box align={Align.Center} pt={1.125} lgPt={1.5}>
                      <ButtonChoose
                        sizeType={Size.LessMedium}
                        colorType={Color.FullBlue}
                      >
                        <Text
                          transform={Transform.Uppercase}
                          color="white"
                          weight={600}
                        >
                          Choose
                        </Text>
                      </ButtonChoose>
                    </Box>
                  </CSSMediaQuery>
                </SelectBoxExtend>
              );
            })}
          </BoxWrapperAll>
        </WrapperOuter>
      </Fragment>
    );
  }
}

export default withTranslation('common')(LicensesTemplate);
